import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { supabase } from "./supabase";
import { initOtaUpdates, markBundleHealthy } from "./ota";

export const isNativeApp = Capacitor.isNativePlatform();

// Custom URL scheme registered in ios/App/App/Info.plist (CFBundleURLTypes).
// Must also be in Supabase Auth > URL Configuration > Redirect URLs.
const NATIVE_AUTH_REDIRECT = "io.tylerslater.tidy://auth-callback";

// Set when this app starts a sign-in, so stray scheme opens are ignored.
const PENDING_SIGN_IN_KEY = "nativeSignInStartedAt";
const PENDING_SIGN_IN_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * Google sign-in for both the web app and the iOS shell. On iOS the OAuth flow
 * runs in an in-app Safari view (Google blocks embedded web views) and returns
 * to the app through the custom URL scheme, handled in handleAuthRedirect.
 */
export async function signInWithGoogle() {
  if (!isNativeApp) {
    return supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
  }

  const result = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: NATIVE_AUTH_REDIRECT, skipBrowserRedirect: true },
  });
  if (result.data.url) {
    localStorage.setItem(PENDING_SIGN_IN_KEY, String(Date.now()));
    await Browser.open({ url: result.data.url, presentationStyle: "popover" });
  }
  return result;
}

function takePendingSignIn(): boolean {
  const startedAt = Number(localStorage.getItem(PENDING_SIGN_IN_KEY));
  localStorage.removeItem(PENDING_SIGN_IN_KEY);
  return startedAt > 0 && Date.now() - startedAt < PENDING_SIGN_IN_MAX_AGE_MS;
}

async function handleAuthRedirect(url: string) {
  if (!url.startsWith(NATIVE_AUTH_REDIRECT)) return;
  if (!takePendingSignIn()) {
    console.warn("Ignoring auth callback that this app did not start");
    return;
  }
  await Browser.close().catch(() => undefined);

  const parsed = new URL(url);
  const params = new URLSearchParams(parsed.hash.replace(/^#/, ""));
  parsed.searchParams.forEach((value, key) => params.set(key, value));

  const errorDescription = params.get("error_description");
  if (errorDescription) {
    console.error("OAuth error:", errorDescription);
    alert(`Login error: ${errorDescription}`);
    return;
  }

  // PKCE (see lib/supabase.ts): only a ?code= is accepted; it is exchanged
  // with the verifier this app stored when the sign-in started.
  const code = params.get("code");
  const { error } = code
    ? await supabase.auth.exchangeCodeForSession(code)
    : { error: new Error("Missing code in auth redirect") };
  if (error) {
    console.error("Failed to complete sign-in:", error);
    alert(`Login error: ${error.message}`);
  }
}

/** One-time native setup before React renders; no-op in the browser. */
export function initNativeShell() {
  if (!isNativeApp) return;

  StatusBar.setStyle({ style: Style.Default }).catch(() => undefined);
  App.addListener("appUrlOpen", ({ url }) => {
    handleAuthRedirect(url).catch((error) =>
      console.error("Auth redirect failed:", error)
    );
  });
  initOtaUpdates();
}

/** Called after React's first successful commit; no-op in the browser. */
export function onAppRendered() {
  if (!isNativeApp) return;
  markBundleHealthy();
  // The splash (launchShowDuration in capacitor.config.ts) is a fallback for
  // a JS boot failure; normally it is hidden here.
  SplashScreen.hide().catch(() => undefined);
}
